import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { PieChart as PieIcon, ChevronLeft, Globe, Monitor, Globe2, TrendingUp } from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { toast } from 'sonner';

const COLORS = ['#6366f1','#22c55e','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f43f5e','#6366f1','#3b82f6','#ec4899','#f97316','#84cc16'];

function formatSecs(s) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// Custom Donut Center Label
function DonutLabel({ cx, cy, total }) {
  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
      <tspan x={cx} dy="-0.5em" fontSize="22" fontWeight="700" fill="#1f2937">{formatSecs(total)}</tspan>
      <tspan x={cx} dy="1.4em" fontSize="11" fill="#6b7280">Total Usage</tspan>
    </text>
  );
}

export default function AppsSummaryPage() {
  const navigate = useNavigate();
  const { fromDate, setFromDate, toDate, setToDate, selectedStaff, setSelectedStaff,
    staffList, timezone, setPreset } = useReportFilters();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('total'); // 'total' | 'per_employee'
  const [selectedApp, setSelectedApp] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/activity-reports/app-usage`, {
        params: {
          start_date: fromDate, end_date: toDate,
          staff_id: selectedStaff === 'all' ? undefined : selectedStaff
        },
        withCredentials: true
      });
      setData(res.data);
      setSelectedApp(null);
    } catch (e) { toast.error('Failed to load apps summary'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [fromDate, toDate, selectedStaff]);

  // Build donut chart data from top apps
  const topApps = (data?.top_apps || []).slice(0, 12);
  const totalSecs = topApps.reduce((a, app) => a + (app.total_duration_seconds || (app.hours || 0) * 3600 || 0), 0);
  const donutData = topApps.map((app, i) => ({
    name: app.app_name || app.app || app.domain,
    value: app.total_duration_seconds || (app.hours || 0) * 3600 || 0,
    color: COLORS[i % COLORS.length],
    category: app.category,
    staff_count: app.staff_count || 0,
  }));

  // Per-employee breakdown for selected app
  const staffUsageList = data?.staff_usage || data?.records || [];
  const appDetails = selectedApp
    ? staffUsageList.map(s => ({
        name: s.staff_name,
        secs: ((s.apps || s.top_apps || []).find(a => (a.app_name || a.app) === selectedApp || (a.domain) === selectedApp)?.duration_seconds
          || ((s.apps || s.top_apps || []).find(a => (a.app_name || a.app) === selectedApp)?.hours || 0) * 3600
          || 0)
      })).filter(s => s.secs > 0).sort((a,b) => b.secs - a.secs)
    : [];

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const { name, value, color, category } = payload[0].payload;
    return (
      <div className="bg-white border rounded-xl shadow-lg p-3 text-sm">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
          <span className="font-semibold">{name}</span>
        </div>
        <p className="text-gray-600">Time: <span className="font-semibold text-gray-900">{formatSecs(value)}</span></p>
        <p className="text-gray-600">Share: <span className="font-semibold">{totalSecs ? ((value/totalSecs)*100).toFixed(1) : 0}%</span></p>
        {category && <Badge className="mt-1 text-xs bg-gray-100 text-gray-700">{category}</Badge>}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="apps-summary-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/reports')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Apps Summary</h1>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
            <Globe className="w-3.5 h-3.5" /><span>{timezone}</span>
          </div>
        </div>
        {/* View toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
          {[['total','Total Usage'],['per_employee','Per Employee']].map(([v,l]) => (
            <button key={v} onClick={() => setViewMode(v)}
              className={`px-3 py-1.5 text-xs rounded-md transition-all font-medium ${viewMode === v ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Preset</Label>
              <div className="flex gap-1">
                {[['today','Today'],['week','This Week'],['month','This Month']].map(([k,l]) => (
                  <Button key={k} size="sm" variant="outline" onClick={() => setPreset(k)} className="text-xs h-8">{l}</Button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">From</Label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-8 text-sm w-36" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">To</Label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-8 text-sm w-36" />
            </div>
            {viewMode === 'per_employee' && (
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Staff</Label>
                <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                  <SelectTrigger className="h-8 text-sm w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {staffList.map(s => <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-12 text-gray-500">Loading apps...</div>}

      {!loading && data && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: App list */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Top Applications</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y max-h-[480px] overflow-y-auto">
                {topApps.length > 0 ? topApps.map((app, i) => {
                  const name = app.app_name || app.app || app.domain || 'Unknown';
                  const secs = app.total_duration_seconds || (app.hours || 0) * 3600 || 0;
                  const pct = totalSecs > 0 ? Math.round(secs / totalSecs * 100) : 0;
                  const color = COLORS[i % COLORS.length];
                  const isSelected = selectedApp === name;

                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedApp(isSelected ? null : name)}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `${color}22` }}>
                        {app.app_name ? <Monitor className="w-4 h-4" style={{ color }} /> : <Globe2 className="w-4 h-4" style={{ color }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-1">
                            <div className="h-1 rounded-full" style={{ width: `${pct}%`, background: color }} />
                          </div>
                          <span className="text-xs text-gray-500 font-mono w-8">{pct}%</span>
                        </div>
                      </div>
                      <div className="text-right ml-2 flex-shrink-0">
                        <p className="text-sm font-bold text-gray-700">{formatSecs(secs)}</p>
                        {(app.staff_count > 0) && <p className="text-xs text-gray-400">{app.staff_count} users</p>}
                      </div>
                    </button>
                  );
                }) : (
                  <div className="py-12 text-center text-gray-400 text-sm">No app usage data</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right: Donut Chart OR Per-employee breakdown */}
          <div className="space-y-4">
            {!selectedApp ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Usage Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {donutData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={320}>
                      <PieChart>
                        <Pie
                          data={donutData}
                          cx="50%" cy="50%"
                          innerRadius={80} outerRadius={130}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {donutData.map((d, i) => (
                            <Cell key={i} fill={d.color} stroke="white" strokeWidth={2} />
                          ))}
                          <DonutLabel cx="50%" cy="50%" total={totalSecs} />
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          formatter={(v) => <span className="text-xs text-gray-600">{v.length > 18 ? v.slice(0,18)+'…' : v}</span>}
                          iconSize={8} iconType="circle"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-gray-400">No data</div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-blue-500" />
                      {selectedApp} — per employee
                    </CardTitle>
                    <button onClick={() => setSelectedApp(null)} className="text-xs text-gray-400 hover:text-gray-600 underline">
                      Back to chart
                    </button>
                  </div>
                </CardHeader>
                <CardContent>
                  {appDetails.length > 0 ? (
                    <div className="space-y-3">
                      {appDetails.map((s, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                            {s.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{s.name}</p>
                            <div className="mt-1 bg-gray-200 rounded-full h-1.5">
                              <div className="bg-blue-500 h-1.5 rounded-full"
                                style={{ width: `${Math.round(s.secs / appDetails[0].secs * 100)}%` }} />
                            </div>
                          </div>
                          <span className="text-sm font-bold text-gray-800 ml-2 flex-shrink-0">{formatSecs(s.secs)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-8">No data for this app</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-indigo-50 rounded-xl text-center">
                <p className="text-xl font-bold text-indigo-600">{topApps.length}</p>
                <p className="text-xs text-gray-500">Apps tracked</p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-xl text-center">
                <p className="text-xl font-bold text-indigo-600">{formatSecs(totalSecs)}</p>
                <p className="text-xs text-gray-500">Total time</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-xl text-center">
                <p className="text-xl font-bold text-purple-600">
                  {Math.max(...(topApps.map(a => a.staff_count || 0)), 0)}
                </p>
                <p className="text-xs text-gray-500">Peak users</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
